/**
 * Boot: mount the player's archives (browser storage, or the dev disc server), then start the
 * engine on the LoD trademark screen.
 */
import { archiveNameOf, archivesInIso, archivesInTome, type IgnoredFile, ignoredReason, isInstallerTome, mountArchives, type MountReport, type NamedSource, type PatchTarget } from '@d2/formats';
import { dataFingerprint, GameData } from '@d2/data';
import type { DataProfile } from '@d2/protocol';
import { installBlockDecoder } from './assets/block-decoder';
import { Assets } from './assets/store';
import { AudioEngine } from './audio/audio';
import { HttpRangeSource } from './boot/sources';
import { showImportScreen } from './boot/import-screen';
import { forgetArchives, storedArchives } from './boot/storage';
import { checkBrowser, hasWebGL2, isMobile, showUnsupported } from './boot/support';
import { loadingPanel } from './boot/loading';
import { App } from './engine/app';
import { bindSettings } from './game/settings-apply';
import { Renderer } from './gfx/renderer';
import { captureAccountLink } from './net/account-link';
import { installErrorReporting } from './net/report';
import { TrademarkScreen } from './ui/screens/trademark';
import { mountSettings, patchFromAddress, sourceDescriptors } from './sp/sources';
import './boot/import.css';

const overlay = document.getElementById('overlay')!;
const canvas = document.getElementById('screen') as HTMLCanvasElement;

const devIgnored: IgnoredFile[] = [];

async function devDiscs(): Promise<NamedSource[]> {
  try {
    const res = await fetch('/__dev_discs/');
    if (!res.ok || !res.headers.get('content-type')?.includes('json')) return [];
    const files = (await res.json()) as { name: string; size: number }[];
    const out: NamedSource[] = [];
    for (const f of files) {
      const url = `/__dev_discs/${encodeURIComponent(f.name)}`;
      sourceDescriptors.push({ kind: 'devdisc', url, name: f.name, size: f.size });
      const src = new HttpRangeSource(url, f.size);
      if (/\.iso$/i.test(f.name)) out.push(...(await archivesInIso(src, f.name, devIgnored)));
      else if (isInstallerTome(f.name)) out.push(...(await archivesInTome(src, f.name, devIgnored)).sources);
      else {
        const n = archiveNameOf(f.name);
        if (n) out.push({ name: n, source: src, origin: f.name });
        else if (ignoredReason(f.name)) devIgnored.push({ name: f.name.toLowerCase(), origin: f.name, reason: ignoredReason(f.name)! });
      }
    }
    return out;
  } catch {
    return [];
  }
}

/** The staged loading status (with the controls card) until the trademark screen starts. */
function stage(text: string, pct?: number): void {
  loadingPanel(overlay).stage(text, pct);
}

/** The loading line while the stored files open: which patch is being applied, if any. */
function patchStage(target: PatchTarget, names: string[]): string {
  if (target === '1.14d' && names.includes('d2patch_114d.exe')) return 'Applying the 1.14d patch…';
  if (target !== 'off' && names.includes('lod_109d.exe') && (target === '1.09d' || !names.includes('patch_d2.mpq'))) return 'Applying the 1.09d patch…';
  return 'Opening your files…';
}

/**
 * Mounts the player's archives. `?import` opens the import screen even when the files are
 * already stored (to add the 1.14d patch or swap discs); `?patch=1.09d` reads the copy as 1.09d
 * and `?patch=off` the discs' own 1.07 data, for realms that run those versions.
 */
async function mount(): Promise<MountReport> {
  const params = new URLSearchParams(location.search);
  const opts = { patch: patchFromAddress(location.search) };
  mountSettings.patch = opts.patch;
  const dev = import.meta.env.DEV ? await devDiscs() : [];
  if (dev.length && params.has('devdiscs')) return mountArchives(dev, { ...opts, ignored: devIgnored });
  let reimport = params.has('import');
  for (;;) {
    const stored = await storedArchives();
    if (stored.length && !reimport)
      stage(patchStage(opts.patch, stored.map((s) => s.name)));
    let report: MountReport;
    let notice: string | undefined;
    try {
      report = await mountArchives(stored, opts);
    } catch (e) {
      // A stored copy that cannot be read (cut short by a full disk): start over from the files.
      console.error(e);
      await forgetArchives();
      stored.length = 0;
      report = await mountArchives([], opts);
      notice = 'Your saved game files were damaged, usually by a full disk. Free some space and load them again.';
    }
    if (report.missing.length === 0 && !reimport) {
      sourceDescriptors.length = 0;
      for (const s of stored) {
        const blob = (s.source as unknown as { blob?: Blob }).blob;
        if (blob) sourceDescriptors.push({ kind: 'blob', name: s.name, blob });
      }
      return report;
    }
    const chosen = await showImportScreen(overlay, { devDiscs: dev.length > 0, preloaded: stored, patch: opts.patch, notice });
    if (reimport) {
      reimport = false;
      const q = new URLSearchParams(location.search);
      q.delete('import');
      history.replaceState(null, '', `${location.pathname}${q.size ? `?${q}` : ''}`);
    }
    // Only "use developer discs" mounts them; imported files are read from storage next round.
    if (chosen === 'dev') {
      const r = await mountArchives(dev, { ...opts, ignored: devIgnored });
      if (r.missing.length === 0) return r;
    }
  }
}

async function main(): Promise<void> {
  installErrorReporting();
  // A reset or confirmation link from the realm's email: kept for the log-in screen.
  captureAccountLink();
  // Developer discs come over HTTP and need no browser storage; everyone else needs all of it.
  const devOnly = import.meta.env.DEV && new URLSearchParams(location.search).has('devdiscs');
  // Phones and tablets see the title page; Play explains that d2o needs a computer.
  if (!devOnly && isMobile()) return void showImportScreen(overlay, { devDiscs: false, mobile: true });
  const problem = devOnly ? (hasWebGL2() ? null : 'webgl2') : await checkBrowser();
  if (problem) return showUnsupported(overlay, problem);
  installBlockDecoder();
  const report = await mount();
  const vfs = report.vfs;
  stage('Loading graphics…');
  const renderer = new Renderer(canvas);
  const assets = new Assets(vfs, renderer);
  await assets.init((s) => stage(`Loading graphics: ${s}…`));
  stage('Loading game data…');
  const data = await GameData.load(vfs);
  // What the realm checks this copy against (see @d2/protocol data-profile).
  const dataProfile: DataProfile = {
    v: 1,
    version: report.version,
    patch: report.patch,
    patchSkipped: report.patchSkipped,
    target: report.target,
    skippedFile: report.skippedFile,
    language: report.language,
    parts: await dataFingerprint(data, vfs),
  };
  stage('Preparing sound…');
  const audio = new AudioEngine(vfs);
  audio.loadSoundTable(data.t.Sounds);
  bindSettings(audio, renderer);
  const app = new App(renderer, assets, audio);
  await app.loadCursors();
  overlay.innerHTML = '';
  (window as unknown as { d2: unknown }).d2 = { app, assets, renderer, audio, vfs, data, dataHash: data.hash, dataProfile, mount: report };
  app.start();
  await app.go(new TrademarkScreen());
}

main().catch((e) => {
  console.error(e);
  stage(`Something went wrong: ${(e as Error).message}`);
  loadingPanel(overlay).el.classList.add('failed');
});
